import { useEffect, useRef, useState } from 'react'
import { RollVariant, ROLL_DIMENSIONS } from '@/types'
import { regularRolls, hotRolls } from '@/assets/rolls'
import { FallingRollConfig, defaultFallingRollConfig } from '@/components/DevTools/FallingRollEditor'

interface FallingRollProps {
  variant: RollVariant
  isHot: boolean
  startX: number        // Starting X offset in pixels from center
  towerHeight: number   // Tower height in pixels
  rollTop?: number
  config?: FallingRollConfig
  isMiss?: boolean
  isEdgeMiss?: boolean
  isRollOff?: boolean
  isWipeout?: boolean
  fallDuration?: number
  horizontalVelocity?: number // px/s — inertia from chopsticks (legacy/demo mode)
  // Explicit landing X in px (if provided, used instead of computing from velocity).
  // Should match server-computed landing position to avoid post-anim jumps.
  landingXOverride?: number
}

const BASE_FALL_DURATION = 600
const INERTIA_FACTOR = 0.25

export default function FallingRoll({ variant, isHot, startX, towerHeight, rollTop = 8, config, isMiss = false, isEdgeMiss = false, isRollOff = false, isWipeout = false, fallDuration, horizontalVelocity = 0, landingXOverride }: FallingRollProps) {
  const [phase, setPhase] = useState<'start' | 'falling' | 'squash' | 'bounce' | 'settled' | 'flyaway' | 'edge_land' | 'edge_fall' | 'roll_off_tip' | 'roll_off_fall' | 'wipeout_fall'>('start')
  const dropStartedAtRef = useRef(Date.now())
  const outcomeFlagsRef = useRef({ isEdgeMiss, isWipeout })
  const cfg = config || defaultFallingRollConfig
  const actualFallDuration = fallDuration || BASE_FALL_DURATION

  const dims = ROLL_DIMENSIONS[variant]
  const currentWidth = dims.width
  const currentHeight = dims.height

  const rollAsset = isHot
    ? hotRolls[variant >= 6 ? (variant - 6) % hotRolls.length : 0]
    : regularRolls[(variant - 1) % regularRolls.length]

  const getEndBottom = () => {
    if (isMiss) return -200
    return cfg.plateBottom + towerHeight
  }
  const endBottom = getEndBottom()

  const getSquash = () => {
    switch (phase) {
      case 'squash': return 'scaleY(0.82) scaleX(1.12)'
      case 'bounce': return 'scaleY(1.06) scaleX(0.96)'
      default: return 'scaleY(1) scaleX(1)'
    }
  }

  // If an explicit landing X is provided (server-computed), use it.
  // Otherwise fall back to velocity-based drift (demo mode).
  const totalDriftPx = horizontalVelocity * (actualFallDuration / 1000) * INERTIA_FACTOR
  const landingX = landingXOverride !== undefined ? landingXOverride : startX + totalDriftPx

  const edgeFallDirection = landingX >= 0 ? 1 : -1

  useEffect(() => {
    outcomeFlagsRef.current = { isEdgeMiss, isWipeout }
  }, [isEdgeMiss, isWipeout])

  useEffect(() => {
    dropStartedAtRef.current = Date.now()
    setPhase('start')
    const fallTimer = setTimeout(() => setPhase('falling'), 30)

    if (isMiss) {
      return () => clearTimeout(fallTimer)
    }

    const squashDelay = actualFallDuration - 50
    const bounceDelay = actualFallDuration + 50
    const settleDelay = actualFallDuration + 150

    const landTimer = setTimeout(() => {
      if (!outcomeFlagsRef.current.isEdgeMiss) setPhase('squash')
    }, squashDelay)
    const bounceTimer = setTimeout(() => {
      if (!outcomeFlagsRef.current.isEdgeMiss && !outcomeFlagsRef.current.isWipeout) setPhase('bounce')
    }, bounceDelay)
    const settleTimer = setTimeout(() => {
      if (!outcomeFlagsRef.current.isEdgeMiss && !outcomeFlagsRef.current.isWipeout) setPhase('settled')
    }, settleDelay)

    return () => {
      clearTimeout(fallTimer); clearTimeout(landTimer)
      clearTimeout(bounceTimer); clearTimeout(settleTimer)
    }
  }, [isMiss, actualFallDuration])

  useEffect(() => {
    const elapsed = Date.now() - dropStartedAtRef.current
    const delayFromDrop = (ms: number) => Math.max(0, ms - elapsed)
    const timers: ReturnType<typeof setTimeout>[] = []

    if (isEdgeMiss) {
      timers.push(setTimeout(() => setPhase('edge_land'), delayFromDrop(actualFallDuration)))
      timers.push(setTimeout(() => setPhase('edge_fall'), delayFromDrop(actualFallDuration + 50)))
    }

    if (isWipeout) {
      timers.push(setTimeout(() => setPhase('squash'), delayFromDrop(actualFallDuration - 40)))
      timers.push(setTimeout(() => setPhase('wipeout_fall'), delayFromDrop(actualFallDuration + 150)))
    }

    if (isRollOff) {
      timers.push(setTimeout(() => setPhase('roll_off_tip'), delayFromDrop(actualFallDuration + 280)))
      timers.push(setTimeout(() => setPhase('roll_off_fall'), delayFromDrop(actualFallDuration + 620)))
    }

    return () => timers.forEach(clearTimeout)
  }, [isEdgeMiss, isRollOff, isWipeout, actualFallDuration])

  const startBottom = `calc(100% - ${rollTop}px - ${currentHeight}px)`

  const getCurrentBottom = (): string => {
    switch (phase) {
      case 'start': return startBottom
      case 'edge_land': return `${endBottom}px`
      case 'edge_fall': return `-300px`
      case 'roll_off_tip': return `${Math.max(-300, endBottom - 8)}px`
      case 'roll_off_fall': return `-300px`
      case 'wipeout_fall': return `-320px`
      default:
        return phase === 'falling' || phase === 'squash' || phase === 'bounce' || phase === 'settled'
          ? `${endBottom}px`
          : startBottom
    }
  }

  // Current X position depending on phase
  const getCurrentX = (): number => {
    switch (phase) {
      case 'start': return startX
      case 'falling':
      case 'squash':
      case 'bounce':
      case 'settled':
      case 'edge_land':
        return landingX
      case 'roll_off_tip':
        return landingX + edgeFallDirection * 34
      case 'roll_off_fall':
        return landingX + edgeFallDirection * 165
      case 'wipeout_fall':
        return landingX + edgeFallDirection * 130
      case 'edge_fall':
        return landingX + edgeFallDirection * 80
      default:
        return startX
    }
  }

  const getTransition = (): string => {
    switch (phase) {
      case 'falling': return `bottom ${actualFallDuration}ms cubic-bezier(0.33, 0, 0.67, 1), left ${actualFallDuration}ms ease-out`
      case 'roll_off_tip': return 'bottom 340ms cubic-bezier(0.22, 1, 0.36, 1), left 340ms cubic-bezier(0.22, 1, 0.36, 1), transform 340ms cubic-bezier(0.22, 1, 0.36, 1)'
      case 'roll_off_fall': return 'bottom 620ms cubic-bezier(0.55, 0.06, 0.68, 0.19), left 620ms cubic-bezier(0.55, 0.06, 0.68, 0.19), transform 620ms cubic-bezier(0.55, 0.06, 0.68, 0.19), opacity 420ms ease-out'
      case 'wipeout_fall': return 'bottom 700ms cubic-bezier(0.55, 0.06, 0.68, 0.19), left 700ms cubic-bezier(0.55, 0.06, 0.68, 0.19), transform 700ms cubic-bezier(0.55, 0.06, 0.68, 0.19), opacity 520ms ease-out'
      case 'edge_fall': return 'bottom 500ms ease-in, left 500ms ease-in, transform 500ms ease-in, opacity 500ms ease-in'
      default: return 'none'
    }
  }

  const getTransform = (): string => {
    if (phase === 'edge_fall') {
      return `translateX(-50%) rotate(${edgeFallDirection * 45}deg)`
    }
    if (phase === 'roll_off_tip') {
      return `translateX(-50%) rotate(${edgeFallDirection * 24}deg)`
    }
    if (phase === 'roll_off_fall') {
      return `translateX(-50%) rotate(${edgeFallDirection * 150}deg) scale(0.96)`
    }
    if (phase === 'wipeout_fall') {
      return `translateX(-50%) rotate(${edgeFallDirection * 115}deg) scale(0.94)`
    }
    return 'translateX(-50%)'
  }

  const getOpacity = (): number => {
    if (phase === 'edge_fall') return 0
    if (phase === 'roll_off_fall') return 0
    if (phase === 'wipeout_fall') return 0
    return 1
  }

  return (
    <div
      className="absolute z-30 pointer-events-none"
      style={{
        width: `${currentWidth}px`,
        height: `${currentHeight}px`,
        bottom: getCurrentBottom(),
        left: `calc(50% + ${getCurrentX()}px)`,
        transform: getTransform(),
        transition: getTransition(),
        opacity: getOpacity(),
        willChange: 'transform, bottom, opacity',
        backfaceVisibility: 'hidden',
      }}
    >
      {isHot && (
        <>
          <div
            className="absolute inset-0 animate-fire-glow"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(255,100,0,0.8) 0%, rgba(255,50,0,0.4) 35%, transparent 60%)',
              transform: 'scale(1.8)',
              borderRadius: '50%',
            }}
          />
          <div
            className="absolute inset-0 animate-fire-glow-delayed"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(255,220,50,0.9) 0%, rgba(255,150,0,0.5) 30%, transparent 55%)',
              transform: 'scale(1.3)',
              borderRadius: '50%',
            }}
          />
        </>
      )}
      <img
        src={rollAsset.src}
        alt={rollAsset.name}
        className="w-full h-full object-contain"
        style={{
          filter: isHot ? 'drop-shadow(0 2px 6px rgba(255,100,0,0.5))' : 'none',
          transform: getSquash(),
          transformOrigin: 'center bottom',
          transition: 'transform 100ms ease-out',
        }}
        draggable={false}
      />
    </div>
  )
}
